import React, {useEffect, useRef} from "react";
import {css} from "@emotion/react";

import {Button} from "@pg-design/button-module";
import {elevation} from "@pg-design/elevation/src/elevation";
import {Container} from "@pg-design/grid-module";
import {flex, onDesktop, pb, pt, pv, textAlign, w100} from "@pg-design/helpers-css";
import {BrandMapPinIcon} from "@pg-design/icons-module";
import {rpAppLink} from "@pg-mono/rp-routes";
import {useUserDevice} from "@pg-mono/user-device";
import {navigationHeight} from "@pg-rp/navigation";

import {useStickyIntersection} from "../../../hooks/use_sticky_intersection";
import {useCookieBotObserver} from "../../../navigation/hooks/use_cookie_bot_observer";
import {SlotOrderType} from "../../../search/components/search_autocomplete/SearchAutocompleteInputWithSlot";
import {SearchFormWithDrawer} from "../../../search/components/SearchFormWithDrawer";
import {searchFormWithDrawerHeight} from "../../../search/constants/search_form_with_drawer_height";
import {tdaDesktopHeight} from "../../../smart_ad_server/constants/tda_desktop_height";
import {useAppDispatch} from "../../../store/hooks";
import {ILatestQuery} from "../../../utils/latest_query";
import {setOfferListingNavigationSticky} from "../../state/offer_listing_slice";

interface IProps {
    mapUrl: string;
    latestQuery?: ILatestQuery;
    hideSearchAndFilter?: boolean;
    hideBottomPadding?: boolean;
}

const stickyHeaderHeight = 88;

export const ListingStickyHeader = (props: IProps) => {
    const {isMobile} = useUserDevice();
    const wrapperRef = useRef<HTMLDivElement>(null);
    const dispatch = useAppDispatch();
    const isSticky = useStickyIntersection(wrapperRef);
    const {isCookieBotOpen} = useCookieBotObserver();

    useEffect(() => {
        dispatch(setOfferListingNavigationSticky(isSticky));
    }, [isSticky]);

    return (
        <div ref={wrapperRef} css={[headerWrapperStyle, headerWrapperStickyStyle(isSticky)]}>
            {isSticky && !isMobile && (
                <div css={stickyHeaderWrapperStyle(isSticky)}>
                    <Container css={containerStyle}>
                        {!props.hideSearchAndFilter && !!props.latestQuery && (
                            <SearchFormWithDrawer
                                listFriendlyLink={({friendlySlug, offerListSubFilterSlug}) => {
                                    return offerListSubFilterSlug
                                        ? rpAppLink.offer.listFriendlySubFilterGeneral({friendlySlug, offerListSubFilter: offerListSubFilterSlug})
                                        : rpAppLink.offer.listFriendly({friendlySlug});
                                }}
                                abroadListFriendlyLink={(params) => rpAppLink.offer.listFriendlyAbroad(params)}
                                multiRegionLink={rpAppLink.offer.multiList}
                                latestQuery={props.latestQuery}
                                sortActive
                                shouldRenderListBackBtn
                                // TODO: Sometimes blend is relative to their elements, navigationHeight should be removed from atoms/DesktopBlend implementation
                                blendTop={`${stickyHeaderHeight - navigationHeight}px`}
                                blendHeightDecrease={`${stickyHeaderHeight + navigationHeight + tdaDesktopHeight * 2 + searchFormWithDrawerHeight}px`}
                                className="isInNavigationBar"
                                slotOrder={SlotOrderType.ROW_REVERSE}
                                hideBottomPadding={props.hideBottomPadding}
                                disableFilterAutoShowcase
                                disableLocationSearch={isCookieBotOpen}
                            />
                        )}
                        <Button href={props.mapUrl} iconLeft={BrandMapPinIcon} variant="brand_icon" size="medium" css={buttonStyle}>
                            Zobacz na mapie
                        </Button>
                    </Container>
                </div>
            )}
        </div>
    );
};

const headerWrapperStyle = css`
    display: none;

    ${onDesktop(css`
        display: block;
        position: sticky;
        top: 0;

        height: ${stickyHeaderHeight}px;
        margin-bottom: -${stickyHeaderHeight}px;
    `)};
`;

const headerWrapperStickyStyle = (isSticky: boolean) =>
    isSticky
        ? css`
              z-index: 22;
          `
        : css`
              z-index: -1;
          `;

const stickyHeaderWrapperStyle = (isSticky: boolean) =>
    isSticky
        ? css`
              ${elevation(4)};
              ${pv(1)};
              background: white;
              width: 100%;
          `
        : css`
              visibility: hidden;
          `;

const buttonStyle = css`
    transition-duration: unset;
    transition-timing-function: unset;
    overflow: initial;
`;

const containerStyle = css`
    ${textAlign("right")};
    ${flex("center", "flex-end")};
    ${w100};

    > div:first-of-type {
        ${w100};
    }

    .isInNavigationBar {
        ${pb(0)};
        > div:nth-of-type(2) {
            ${pt(0)};
        }
    }
`;
